import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';

export function MrpRunPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [runId, setRunId] = useState(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [error, setError] = useState(null);

  const runs = useQuery({ queryKey: ['inventory', 'mrp-runs'], queryFn: () => inventoryApi.listMrpRuns().then((r) => r.items) });
  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items) });
  const detail = useQuery({ queryKey: ['inventory', 'mrp-run', runId], queryFn: () => inventoryApi.getMrpRun(runId).then((r) => r.items), enabled: Boolean(runId) });
  const run = detail.data;
  const items = run?.items || [];

  useEffect(() => { if (!warehouseId && (warehouses.data || []).length) setWarehouseId(warehouses.data[0].id); }, [warehouses.data, warehouseId]);

  const start = useMutation({
    mutationFn: () => inventoryApi.runMrp(),
    onSuccess: (res) => { setError(null); queryClient.invalidateQueries({ queryKey: ['inventory', 'mrp-runs'] }); if (res?.data?.id) setRunId(res.data.id); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });
  const convert = useMutation({
    mutationFn: () => inventoryApi.convertMrp(runId, { warehouseId: warehouseId || null }),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ['inventory', 'mrp-runs'] }); queryClient.invalidateQueries({ queryKey: ['inventory', 'mrp-run', runId] }); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  const runColumns = [
    { key: 'number', label: t('mr.col.run'), render: (r) => <Button size="sm" variant="ghost" onClick={() => setRunId(r.id)}><span className="mono">{r.runNumber}</span></Button> },
    { key: 'date', label: t('mr.col.date'), render: (r) => formatDate(r.runDate) },
    { key: 'count', label: t('mr.col.count'), align: 'right', render: (r) => r.itemCount },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={r.status === 'CONVERTED' ? 'success' : 'info'}>{t(`mr.status.${r.status}`)}</Badge> },
  ];

  const itemColumns = [
    { key: 'product', label: t('mr.col.product'), render: (r) => <span><strong>{r.productSku}</strong> <span className="muted text-sm">{r.productName}</span></span> },
    { key: 'onHand', label: t('mr.col.onHand'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.onHand)}</span> },
    { key: 'onOrder', label: t('mr.col.onOrder'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.onOrder)}</span> },
    { key: 'backordered', label: t('mr.col.backordered'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.backordered)}</span> },
    { key: 'reorder', label: t('mr.col.reorder'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.reorderPoint)}</span> },
    { key: 'net', label: t('mr.col.net'), align: 'right', render: (r) => <span className="mono num" style={{ color: 'var(--color-danger, #dc2626)' }}><strong>{formatNumber(r.netRequirement)}</strong></span> },
    { key: 'suggested', label: t('mr.col.suggested'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.suggestedQty)}</span> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('mr.title')}</h1>
          <p className="muted">{t('mr.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate()}>{start.isPending ? `${t('mr.action.run')}…` : t('mr.action.run')}</Button>
        </div>
      </header>

      {error && <div className="alert danger" style={{ marginBottom: 'var(--space-3)' }}>{error}</div>}

      <div className="settings-divider">{t('mr.runs')}</div>
      <Table columns={runColumns} rows={runs.data || []} isLoading={runs.isLoading} emptyMessage={t('mr.noRuns')} />

      {run && (
        <>
          <div className="card-grid" style={{ marginTop: 'var(--space-4)' }}>
            <MetricCard label={t('mr.col.run')} value={run.runNumber} />
            <MetricCard label={t('mr.metric.proposals')} value={String(items.length)} trendVariant={items.length > 0 ? 'warning' : 'success'} />
            <MetricCard label={t('col.status')} value={t(`mr.status.${run.status}`)} trendVariant={run.status === 'CONVERTED' ? 'success' : 'info'} />
          </div>
          <div className="settings-divider">{t('mr.proposals')} · {run.runNumber}</div>
          <Table columns={itemColumns} rows={items} isLoading={detail.isLoading} emptyMessage={t('mr.noProposals')} />
          {run.status === 'OPEN' && items.length > 0 && (
            <div className="card" style={{ padding: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
              <div className="field-row" style={{ alignItems: 'flex-end' }}>
                <label className="field" style={{ maxWidth: 240 }}><span className="field-label">{t('mr.warehouse')}</span>
                  <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                    {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
                  </select>
                </label>
                <Button variant="primary" disabled={convert.isPending} onClick={() => convert.mutate()}>{t('mr.action.convert')}</Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
